// Mobile port of the web Calendar Configuration (Settings → Calendar
// Configuration → General). Reads/writes `user_settings.calendar_page`
// directly via useSettingColumn — same source of truth as web's
// CalendarPageConfig (src/interfaces/settings.ts).
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { makeThemedStyles } from '../../v10/theme';
import { SectionTitle, Chip, Field, GlassCard } from '../../v10/ui';
import { useSettingColumn } from './useSettingColumn';

interface CalendarPageConfig {
  dayStartTime: string; // HH:mm
  dayEndTime: string; // HH:mm
  timeIntervalMinutes: 30 | 60;
  timeFormat: '12hr' | '24hr';
}

const FALLBACK: CalendarPageConfig = {
  dayStartTime: '06:00',
  dayEndTime: '22:00',
  timeIntervalMinutes: 30,
  timeFormat: '12hr',
};

export default function CalendarEditor({ userId }: { userId: string | undefined }) {
  const styles = useStyles();
  const { value, setValue, loading } = useSettingColumn<CalendarPageConfig>(userId, 'calendar_page', FALLBACK);

  const patch = (next: Partial<CalendarPageConfig>) => {
    setValue({ ...value, ...next });
  };

  if (loading) {
    return <Text style={styles.hint}>Loading…</Text>;
  }

  return (
    <GlassCard style={styles.card}>
      <SectionTitle style={styles.sectionGap}>Day Window</SectionTitle>
      <Text style={styles.hint}>Start and end time shown on the calendar's day view (24hr "HH:mm").</Text>
      <View style={styles.row}>
        <View style={styles.field}>
          <Text style={styles.label}>Start</Text>
          <Field
            inputStyle={styles.input}
            value={value.dayStartTime}
            onChangeText={(t) => patch({ dayStartTime: t })}
            placeholder="06:00"
            autoCapitalize="none"
          />
        </View>
        <View style={styles.field}>
          <Text style={styles.label}>End</Text>
          <Field
            inputStyle={styles.input}
            value={value.dayEndTime}
            onChangeText={(t) => patch({ dayEndTime: t })}
            placeholder="22:00"
            autoCapitalize="none"
          />
        </View>
      </View>

      <SectionTitle style={styles.sectionGap}>Time Interval</SectionTitle>
      <View style={styles.toggleRow}>
        <Chip label="30 min" active={value.timeIntervalMinutes === 30} onPress={() => patch({ timeIntervalMinutes: 30 })} />
        <Chip label="1 hour" active={value.timeIntervalMinutes === 60} onPress={() => patch({ timeIntervalMinutes: 60 })} />
      </View>

      <SectionTitle style={styles.sectionGap}>Time Format</SectionTitle>
      <View style={styles.toggleRow}>
        <Chip label="12hr" active={value.timeFormat === '12hr'} onPress={() => patch({ timeFormat: '12hr' })} />
        <Chip label="24hr" active={value.timeFormat === '24hr'} onPress={() => patch({ timeFormat: '24hr' })} />
      </View>
    </GlassCard>
  );
}

const useStyles = makeThemedStyles(({ t, radii }) =>
  StyleSheet.create({
  sectionGap: { marginTop: 18 },
  card: {
    padding: 14,
    marginTop: 8,
  },
  hint: { fontSize: 12, color: t.ivorySoft, marginTop: 6, fontStyle: 'italic' },
  row: { flexDirection: 'row', gap: 10, marginTop: 10 },
  field: { flex: 1 },
  label: { fontSize: 12, fontWeight: '700', color: t.ivory, marginBottom: 4 },
  input: {
  },
  toggleRow: { flexDirection: 'row', gap: 10, marginTop: 10 },
}),
);
